<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="goBack">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Détails de l'inventaire</h1>
          <div v-if="inventory" class="flex items-center gap-2 mt-0.5">
            <span class="text-xs text-gray-400 font-mono">{{ inventory.reference_number }}</span>
            <span :class="[
              'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
              statusBadgeClass
            ]">{{ getStatusLabel(inventory.status) }}</span>
          </div>
        </div>
      </div>
      <div v-if="inventory" class="flex items-center gap-2">
        <button v-if="canFinalizeInventory"
          class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="isProcessing"
          @click="finalizeInventory">
          <i class="fas fa-check text-[10px]"></i>
          Finaliser l'inventaire
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-20">
      <span class="w-10 h-10 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></span>
      <p class="text-sm text-gray-400">Chargement des détails...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex flex-col items-center justify-center py-20">
      <div class="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
        <i class="fas fa-exclamation-circle text-red-400 text-xl"></i>
      </div>
      <p class="text-sm text-gray-500 mb-4">{{ error }}</p>
      <button @click="loadInventory"
        class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors">
        <i class="fas fa-redo text-[10px]"></i>
        Réessayer
      </button>
    </div>

    <!-- Contenu -->
    <div v-else-if="inventory">

      <!-- Grille de stats -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-boxes text-cyan-600 text-sm"></i>
            </div>
            <div>
              <p class="text-2xl font-bold text-gray-900 leading-none">{{ inventory.total_items || 0 }}</p>
              <p class="text-[11px] text-gray-400 mt-0.5">Produits total</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-check text-emerald-500 text-sm"></i>
            </div>
            <div>
              <p class="text-2xl font-bold text-gray-900 leading-none">{{ inventory.counted_items || 0 }}</p>
              <p class="text-[11px] text-gray-400 mt-0.5">Comptés</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-clock text-amber-500 text-sm"></i>
            </div>
            <div>
              <p class="text-2xl font-bold text-gray-900 leading-none">{{ inventory.pending_items || 0 }}</p>
              <p class="text-[11px] text-gray-400 mt-0.5">En attente</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-chart-pie text-primary text-sm"></i>
            </div>
            <div>
              <p class="text-2xl font-bold text-primary leading-none">{{ inventory.progress_percentage || 0 }}%</p>
              <p class="text-[11px] text-gray-400 mt-0.5">Progression</p>
            </div>
          </div>
          <!-- Barre de progression -->
          <div class="mt-3 w-full bg-gray-100 rounded-full h-1.5">
            <div class="bg-primary h-1.5 rounded-full transition-all duration-500"
              :style="{ width: (inventory.progress_percentage || 0) + '%' }"></div>
          </div>
        </div>
      </div>

      <!-- Informations détaillées -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <!-- Infos principales -->
        <div class="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b bg-gray-50/80">
            <h3 class="text-sm font-semibold text-gray-700">Informations</h3>
          </div>
          <div class="p-5">
            <div class="grid grid-cols-2 md:grid-cols-3 gap-y-5 gap-x-6">
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1">Référence</p>
                <p class="text-sm font-semibold text-gray-900 font-mono">{{ inventory.reference_number }}</p>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1">Statut</p>
                <span :class="[
                  'inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold',
                  statusBadgeClass
                ]">{{ getStatusLabel(inventory.status) }}</span>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1">Créé par</p>
                <div class="flex items-center gap-2">
                  <div class="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-user text-primary text-[8px]"></i>
                  </div>
                  <p class="text-sm font-medium text-gray-700">{{ inventory.user?.name || 'N/A' }}</p>
                </div>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1">Date de création</p>
                <p class="text-sm text-gray-700">{{ formatDate(inventory.created_at) }}</p>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1">Date de début</p>
                <p class="text-sm text-gray-700">{{ inventory.started_at ? formatDate(inventory.started_at) : '-' }}</p>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1">Date de fin</p>
                <p class="text-sm text-gray-700">{{ inventory.completed_at ? formatDate(inventory.completed_at) : '-' }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Résumé rapide -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b bg-primary/5 border-primary/10">
            <h3 class="text-sm font-semibold text-primary">Résumé</h3>
          </div>
          <div class="p-5 space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-box text-gray-400 text-[10px]"></i>
                </span>
                Total produits
              </span>
              <span class="text-sm font-semibold text-gray-900">{{ inventory.total_items || 0 }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-check text-emerald-500 text-[10px]"></i>
                </span>
                Comptés
              </span>
              <span class="text-sm font-bold text-emerald-600">{{ inventory.counted_items || 0 }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-clock text-amber-500 text-[10px]"></i>
                </span>
                En attente
              </span>
              <span class="text-sm font-bold text-amber-600">{{ inventory.pending_items || 0 }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-chart-pie text-primary text-[10px]"></i>
                </span>
                Progression
              </span>
              <span class="text-sm font-bold text-primary">{{ inventory.progress_percentage || 0 }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Produits de l'inventaire -->
      <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <!-- Header + Filtres -->
        <div class="px-5 py-3 border-b bg-gray-50/80 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h3 class="text-sm font-semibold text-gray-700">
            Produits de l'inventaire
            <span class="text-gray-400 font-normal ml-1">({{ filteredItems.length }})</span>
          </h3>
          <div class="flex flex-wrap items-center gap-2">
            <div class="relative">
              <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]"></i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Rechercher..."
                class="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300 w-48"
              />
            </div>
            <select
              v-model="filterStatus"
              class="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 text-gray-600">
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="counted">Comptés</option>
              <option value="adjusted">Ajustés</option>
              <option value="rejected">Rejetés</option>
              <option value="approved">Approuvés</option>
            </select>
            <select
              v-model="filterDifference"
              class="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 text-gray-600">
              <option value="">Toutes les différences</option>
              <option value="match">Aucun écart</option>
              <option value="surplus">Surplus</option>
              <option value="deficit">Déficit</option>
            </select>
          </div>
        </div>

        <!-- Bulk actions -->
        <div v-if="selectedItems.length > 0"
          class="mx-4 mt-3 flex items-center justify-between px-4 py-2.5 bg-primary/5 border border-primary/10 rounded-lg">
          <span class="text-xs font-medium text-primary">
            {{ selectedItems.length }} produit(s) sélectionné(s)
          </span>
          <div class="flex items-center gap-2">
            <button @click="bulkRecount"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              :disabled="isProcessing">
              <i class="fas fa-sync text-[9px]"></i>
              Recompter
            </button>
            <button @click="bulkApplyQuantities"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors"
              :disabled="isProcessing || !canApplyQuantities">
              <i class="fas fa-check text-[9px]"></i>
              Appliquer les quantités
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-100">
                <th class="w-10 px-4 py-3 text-center">
                  <input type="checkbox"
                    :checked="isAllSelected"
                    @change="toggleSelectAll"
                    :disabled="filteredItems.length === 0"
                    class="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary/30 cursor-pointer" />
                </th>
                <th class="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Produit</th>
                <th class="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Catégorie</th>
                <th class="px-4 py-3 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Théorique</th>
                <th class="px-4 py-3 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Réel</th>
                <th class="px-4 py-3 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Différence</th>
                <th class="px-4 py-3 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Statut</th>
              </tr>
            </thead>
            <tbody v-if="filteredItems.length > 0">
              <tr v-for="item in filteredItems" :key="item.id"
                class="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                <td class="w-10 px-4 py-3 text-center">
                  <input type="checkbox" :value="item.id" v-model="selectedItems"
                    class="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary/30 cursor-pointer" />
                </td>
                <td class="px-4 py-3">
                  <div>
                    <p class="text-sm font-medium text-gray-900">{{ item.product?.name }}</p>
                    <p v-if="item.product?.sku" class="text-[10px] text-gray-400 font-mono mt-0.5">SKU: {{ item.product.sku }}</p>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <span v-if="item.product?.category?.name"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                    {{ item.product.category.name }}
                  </span>
                  <span v-else class="text-xs text-gray-300">-</span>
                </td>
                <td class="px-4 py-3 text-center">
                  <span class="text-sm text-gray-500 font-medium">
                    {{ item.expected_quantity }} {{ item.product?.unit || '' }}
                  </span>
                </td>
                <td class="px-4 py-3 text-center">
                  <span v-if="item.actual_quantity !== null" class="text-sm font-semibold text-gray-900">
                    {{ item.actual_quantity }} {{ item.product?.unit || '' }}
                  </span>
                  <span v-else class="text-xs text-gray-300">-</span>
                </td>
                <td class="px-4 py-3 text-center">
                  <span v-if="item.difference !== null" :class="[
                    'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold',
                    item.difference === 0 ? 'bg-gray-100 text-gray-500' :
                    item.difference > 0 ? 'bg-emerald-50 text-emerald-700' :
                    'bg-red-50 text-red-700'
                  ]">
                    {{ item.difference > 0 ? '+' : '' }}{{ item.difference }}
                  </span>
                  <span v-else class="text-xs text-gray-300">-</span>
                </td>
                <td class="px-4 py-3 text-center">
                  <span :class="[
                    'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
                    getItemStatusBadgeClass(item.status)
                  ]">{{ getItemStatusLabel(item.status) }}</span>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="7" class="py-16 text-center">
                  <div class="flex flex-col items-center">
                    <div class="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                      <i class="fas fa-inbox text-gray-300 text-lg"></i>
                    </div>
                    <p class="text-sm text-gray-400">Aucun produit trouvé</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal"
          class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          @click.self="closeModal">
          <div class="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
            <!-- Icon -->
            <div class="flex justify-center mb-4">
              <div :class="[
                'w-14 h-14 rounded-full flex items-center justify-center',
                modalData.iconType === 'warning' ? 'bg-amber-50' : 'bg-emerald-50'
              ]">
                <i :class="[
                  'text-xl',
                  modalData.iconType === 'warning' ? 'fas fa-exclamation-triangle text-amber-500' : 'fas fa-check text-emerald-500'
                ]"></i>
              </div>
            </div>

            <h3 class="text-base font-bold text-gray-900 text-center mb-1">{{ modalData.title }}</h3>
            <p class="text-sm text-gray-500 text-center mb-4">{{ modalData.message }}</p>

            <!-- Details -->
            <div v-if="modalData.details" class="bg-gray-50 rounded-lg p-4 mb-5 space-y-2">
              <div v-for="(detail, index) in modalData.details" :key="index"
                class="flex items-start gap-2 text-xs text-gray-500">
                <i class="fas fa-circle text-[4px] text-primary mt-1.5 flex-shrink-0"></i>
                <span>{{ detail }}</span>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex gap-3">
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                @click="closeModal">
                {{ modalData.cancelText || 'Annuler' }}
              </button>
              <button
                :class="[
                  'flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                  modalData.confirmClass === 'btn-warning' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-primary hover:bg-primary-dark'
                ]"
                :disabled="isProcessing"
                @click="confirmModalAction">
                <span v-if="isProcessing" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block mr-1"></span>
                {{ modalData.confirmText || 'Confirmer' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import physicalInventoryService from '@/services/physicalInventoryService';
import { useToastStore } from '@/store/modules/toastStore';

export default {
    name: 'InventoryDetails',
    setup() {
        const toastStore = useToastStore();
        return { toastStore };
    },
    data() {
        return {
            loading: false,
            error: null,
            inventory: null,
            searchQuery: '',
            filterStatus: '',
            filterDifference: '',
            selectedItems: [],
            isProcessing: false,
            showModal: false,
            modalData: {
                title: '',
                message: '',
                details: null,
                icon: false,
                iconType: '',
                confirmText: '',
                cancelText: '',
                confirmClass: '',
                action: null
            }
        }
    },
    computed: {
        statusBadgeClass() {
            if (!this.inventory) return '';
            const classes = {
                draft: 'bg-gray-100 text-gray-600',
                in_progress: 'bg-blue-50 text-blue-700',
                completed: 'bg-amber-50 text-amber-700',
                approved: 'bg-emerald-50 text-emerald-700',
                rejected: 'bg-red-50 text-red-700'
            };
            return classes[this.inventory.status] || 'bg-gray-100 text-gray-600';
        },
        filteredItems() {
            if (!this.inventory || !this.inventory.items) return [];

            let items = [...this.inventory.items];

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                items = items.filter(item => {
                    return item.product?.name?.toLowerCase().includes(query) ||
                        item.product?.sku?.toLowerCase().includes(query);
                });
            }

            if (this.filterStatus) {
                items = items.filter(item => item.status === this.filterStatus);
            }

            if (this.filterDifference) {
                items = items.filter(item => {
                    if (this.filterDifference === 'match') {
                        return item.difference === 0;
                    } else if (this.filterDifference === 'surplus') {
                        return item.difference > 0;
                    } else if (this.filterDifference === 'deficit') {
                        return item.difference < 0;
                    }
                    return true;
                });
            }

            return items;
        },
        isAllSelected() {
            return this.filteredItems.length > 0 &&
                this.selectedItems.length === this.filteredItems.length;
        },
        canApplyQuantities() {
            if (this.selectedItems.length === 0) return false;
            const selectedItemsData = this.inventory.items.filter(item =>
                this.selectedItems.includes(item.id)
            );
            return selectedItemsData.every(item =>
                item.status === 'counted' && item.actual_quantity !== null
            );
        },
        canFinalizeInventory() {
            if (!this.inventory || !this.inventory.items || this.inventory.items.length === 0) {
                return false;
            }

            if (!['in_progress', 'completed'].includes(this.inventory.status)) {
                return false;
            }

            return this.inventory.items.every(item => item.status === 'adjusted');
        }
    },
    mounted() {
        this.loadInventory();
    },
    methods: {
        async loadInventory() {
            this.loading = true;
            this.error = null;

            try {
                const inventoryId = this.$route.params.id;
                this.inventory = await physicalInventoryService.getInventoryById(inventoryId);
            } catch (error) {
                console.error('Erreur lors du chargement:', error);
                this.error = 'Impossible de charger les détails de l\'inventaire';
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des détails'
                });
            } finally {
                this.loading = false;
            }
        },

        goBack() {
            this.$router.push({ name: 'inventory-history' });
        },

        getStatusLabel(status) {
            return physicalInventoryService.getStatusLabel(status);
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        getItemStatusLabel(status) {
            const labels = {
                pending: 'En attente',
                counted: 'Compté',
                adjusted: 'Ajusté',
                rejected: 'Rejeté',
                approved: 'Approuvé'
            };
            return labels[status] || status;
        },

        getItemStatusBadgeClass(status) {
            const classes = {
                pending: 'bg-amber-50 text-amber-700',
                counted: 'bg-blue-50 text-blue-700',
                adjusted: 'bg-gray-100 text-gray-600',
                rejected: 'bg-red-50 text-red-700',
                approved: 'bg-emerald-50 text-emerald-700'
            };
            return classes[status] || 'bg-gray-100 text-gray-600';
        },

        toggleSelectAll() {
            if (this.isAllSelected) {
                this.selectedItems = [];
            } else {
                this.selectedItems = this.filteredItems.map(item => item.id);
            }
        },

        bulkRecount() {
            if (this.selectedItems.length === 0) return;

            this.modalData = {
                title: 'Recompter les produits',
                message: `Êtes-vous sûr de vouloir recompter ${this.selectedItems.length} produit(s) ?`,
                details: [
                    'Les produits seront remis en attente',
                    'L\'inventaire repassera en cours'
                ],
                icon: true,
                iconType: 'warning',
                confirmText: 'Recompter',
                cancelText: 'Annuler',
                confirmClass: 'btn-warning',
                action: this.executeRecount
            };
            this.showModal = true;
        },

        async executeRecount() {
            this.isProcessing = true;
            try {
                await physicalInventoryService.bulkRecount(
                    this.inventory.id,
                    this.selectedItems
                );

                this.closeModal();
                this.toastStore.addToast({
                    type: 'success',
                    message: `${this.selectedItems.length} produit(s) marqué(s) pour recomptage`
                });

                this.selectedItems = [];
                await this.loadInventory();
            } catch (error) {
                console.error('Erreur lors du recomptage:', error);
                this.closeModal();
                this.toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors du recomptage des produits'
                });
            } finally {
                this.isProcessing = false;
            }
        },

        bulkApplyQuantities() {
            if (this.selectedItems.length === 0 || !this.canApplyQuantities) return;

            const selectedItemsData = this.inventory.items.filter(item =>
                this.selectedItems.includes(item.id)
            );

            const itemsWithDifference = selectedItemsData.filter(item => item.difference !== 0);

            this.modalData = {
                title: 'Appliquer les quantités',
                message: `Appliquer les quantités inventoriées pour ${this.selectedItems.length} produit(s) ?`,
                details: [
                    `${itemsWithDifference.length} produit(s) ont des écarts qui seront ajustés`,
                    'Cette action créera des transactions d\'inventaire',
                    'Les stocks seront automatiquement ajustés'
                ],
                icon: true,
                iconType: 'warning',
                confirmText: 'Appliquer',
                cancelText: 'Annuler',
                confirmClass: 'btn-success',
                action: this.executeApplyQuantities
            };
            this.showModal = true;
        },

        async executeApplyQuantities() {
            this.isProcessing = true;
            try {
                await physicalInventoryService.bulkApplyInventoryAdjustments(
                    this.inventory.id,
                    this.selectedItems
                );

                this.closeModal();
                this.toastStore.addToast({
                    type: 'success',
                    message: `Quantités appliquées pour ${this.selectedItems.length} produit(s)`
                });

                this.selectedItems = [];
                await this.loadInventory();
            } catch (error) {
                console.error('Erreur lors de l\'application:', error);
                this.closeModal();
                this.toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors de l\'application des quantités'
                });
            } finally {
                this.isProcessing = false;
            }
        },

        finalizeInventory() {
            this.modalData = {
                title: 'Finaliser l\'inventaire',
                message: 'Êtes-vous sûr de vouloir finaliser cet inventaire ?',
                details: [
                    'Tous les produits ont été ajustés',
                    'L\'inventaire sera marqué comme terminé',
                    'Cette action est irréversible'
                ],
                icon: true,
                iconType: 'success',
                confirmText: 'Finaliser',
                cancelText: 'Annuler',
                confirmClass: 'btn-success',
                action: this.executeFinalizeInventory
            };
            this.showModal = true;
        },

        async executeFinalizeInventory() {
            this.isProcessing = true;
            try {
                await physicalInventoryService.completeInventory(this.inventory.id);

                this.closeModal();
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire finalisé avec succès'
                });

                await this.loadInventory();
            } catch (error) {
                console.error('Erreur lors de la finalisation:', error);
                this.closeModal();
                this.toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors de la finalisation de l\'inventaire'
                });
            } finally {
                this.isProcessing = false;
            }
        },

        closeModal() {
            this.showModal = false;
            this.modalData = {
                title: '',
                message: '',
                details: null,
                icon: false,
                iconType: '',
                confirmText: '',
                cancelText: '',
                confirmClass: '',
                action: null
            };
        },

        confirmModalAction() {
            if (this.modalData.action && typeof this.modalData.action === 'function') {
                this.modalData.action();
            }
        }
    }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.2s ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
    transition: transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}
.modal-enter-from > div {
    transform: scale(0.95);
}
.modal-leave-to > div {
    transform: scale(0.95);
}
</style>
